"use client";

import { Mountain, Coffee, Route, Satellite, Landmark, Tent } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MetalRoofingBenefits() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const benefits = [{
    index: "01",
    icon: Mountain,
    title: "Superior Hail Resistance",
    body: "Engineered to withstand Calgary’s severe hailstorms with significantly greater impact resistance than traditional asphalt shingles."
  }, {
    index: "02",
    icon: Coffee,
    title: "Energy Efficiency",
    body: "Reflective coatings help reduce heat absorption, improving comfort and reducing cooling demands during summer months."
  }, {
    index: "03",
    icon: Route,
    title: "Backed by Lifetime Warranty",
    body: "Manufacturer-backed warranty coverage designed to protect your investment for decades without the need for frequent roof replacements."
  }, {
    index: "04",
    icon: Satellite,
    title: "Low Maintenance",
    body: "No cracked shingles, missing granules, curling edges, or frequent repairs are commonly associated with asphalt roofing."
  }, {
    index: "05",
    icon: Landmark,
    title: "Enhanced Curb Appeal & Home Value",
    body: "A premium roofing system that improves the appearance of your home while increasing long-term property value."
  }, {
    index: "06",
    icon: Tent,
    title: "High Wind Performance",
    body: "Advanced fastening systems and interlocking profiles provide exceptional wind resistance in Alberta’s demanding climate."
  }];
  return <section data-section-id="1437" id="metalroofingbenefits" className="relative bg-background text-foreground overflow-hidden ![padding:30px_0px_50px_0px]">
      {}
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-muted/50 border-t border-border overflow-hidden flex items-center">
        <div className="flex whitespace-nowrap md:animate-[ticker_40s_linear_infinite]">
          {[0, 1].map(dup => <div key={dup} data-index={dup} className="flex shrink-0">
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground px-8">CALGARY</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground px-8">AIRDRIE</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground px-8">OKOTOKS</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground px-8">COCHRANE</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground px-8">CHESTERMERE</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
            </div>)}
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={headRef} className={`lg:sticky lg:top-32 relative transition-all duration-[700ms] ease-out ${headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
          }}>
              {}
              <div className="pointer-events-none select-none absolute -top-8 -left-2 font-serif italic font-light text-primary/[0.08] leading-none" style={{
              fontSize: "clamp(8rem, 15vw, 15rem)"
            }} aria-hidden="true">
                02
              </div>

              {}
              <p className="relative font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground mb-10">PREMIUM EXTERIOR SOLUTIONS — WHY CHOOSE US</p>

              {}
              <div className="relative flex items-center mb-10">
                <div className="h-px w-20 bg-primary" />
                <div className="w-1.5 h-1.5 bg-primary rounded-lg" />
              </div>

              {}
              <h2 className="relative font-default font-light text-foreground leading-[0.96] tracking-[-0.025em] mb-10" style={{
              fontSize: "clamp(2.5rem, 5.5vw, 5rem)"
            }}>
                Why homeowners{" "}
                <span className="italic text-primary font-light">trust us.</span>
              </h2>

              {}
              <p className="relative max-w-[320px] text-muted-foreground leading-[1.55] !m-0" style={{
              fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)"
            }}>
                MetalLine Exteriors is the preferred choice for premium renovations. We combine high-end materials with expert craftsmanship to ensure your home stands out and stays protected for a lifetime.
              </p>

              {}
              
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <ul className="flex flex-col">
              {benefits.map((benefit, i) => {
              const Icon = benefit.icon;
              return <BenefitRow key={i} i={i} benefit={benefit} Icon={Icon} isLast={i === benefits.length - 1} />;
            })}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}
function BenefitRow({
  i,
  benefit,
  Icon,
  isLast
}: {
  i: number;
  benefit: {
    index: string;
    title: string;
    body: string;
  };
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLLIElement>({
    threshold: 0.15
  });
  return <li ref={ref} data-index={i} className="group relative grid grid-cols-1 gap-4 transition-all duration-[700ms] opacity-100 translate-y-0 !p-0 md:grid-cols-12 md:gap-6" style={{
    transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
    transitionDelay: `${i * 90}ms`
  }}>
      {}
      <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-4 md:gap-6">
        
        <div className="relative w-6 h-6 flex items-center justify-center">
          <Icon className="lucide lucide-tent lucide lucide-tent text-primary transition-all duration-[260ms] ease-out group-hover:fill-primary/20 !w-[30px] !h-[30px]" strokeWidth={1.25} />
        </div>
      </div>

      {}
      <div className="md:col-span-10">
        <h3 className="font-default font-normal text-foreground leading-[1.15] tracking-[-0.015em] mb-5 transition-transform duration-[260ms] ease-out group-hover:-translate-y-1" style={{
        fontSize: "clamp(1.375rem, 1.8vw, 1.75rem)"
      }}>
          {benefit.title}
        </h3>
        <p className="max-w-[480px] text-muted-foreground leading-[1.6] mb-6" style={{
        fontSize: "clamp(0.9375rem, 1vw, 1rem)"
      }}>
          {benefit.body}
        </p>

        {}
        {!isLast && <div className="flex items-center">
            <div className="h-px w-20 bg-primary" />
            <div className="w-1.5 h-1.5 bg-primary rounded-lg" />
          </div>}
      </div>
    </li>;
}